import { useState, useRef, useEffect, useCallback } from 'react';
import { Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  pointerWithin,
  CollisionDetection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckSquare,
  Plus,
  Check,
  MoreVertical,
  Edit2,
  Maximize2,
  Trash2,
  GripVertical,
  Calendar,
  AlertCircle,
  Clock,
  MessageSquare,
  ArrowUpDown,
  Filter,
  X,
  Search,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  UserCircle,
  Archive,
  ArchiveRestore,
  Lock,
  Users,
} from 'lucide-react';
import { tasksApi, projectsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTaskStatusConfigs } from '@/hooks/useTaskStatusConfigs';
import { ViewToggle, TaskView } from '@/components/tasks/ViewToggle';
import { CompactBoard } from '@/components/tasks/CompactBoard';
import { TaskListTakeover } from '@/components/tasks/TaskListTakeover';
import { StatusPicker } from '@/components/tasks/pickers/StatusPicker';
import type { StatusConfig } from '@/components/tasks/pickers/StatusPicker';
import { useAuthStore, getIsSystemAdmin } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { CreateTaskListModal } from '@/components/modals/CreateTaskListModal';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { CompletionCelebration } from '@/components/ui/CompletionCelebration';

const priorityConfig = {
  LOW: { color: 'text-grey-500', label: 'Low' },
  MEDIUM: { color: 'text-blue-500', label: 'Medium' },
  HIGH: { color: 'text-orange-500', label: 'High' },
  URGENT: { color: 'text-error', label: 'Urgent' },
};

type SortOption = 'default' | 'due-date-asc' | 'due-date-desc' | 'priority';

const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

const sortLabels: Record<SortOption, string> = {
  'default': 'Default',
  'due-date-asc': 'Due Date (Earliest)',
  'due-date-desc': 'Due Date (Latest)',
  'priority': 'Priority (Highest)',
};

// Task Actions dropdown menu
function TaskActions({
  onEdit,
  onDelete
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="p-1 hover:bg-grey-100 rounded transition-colors"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <MoreVertical className="w-4 h-4 text-grey-400" />
      </button>

      {open && (
        <div className="absolute right-0 bottom-full mb-1 w-32 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// Sortable Task Item
interface SortableTaskProps {
  task: any;
  onClick?: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpdateStatus?: (statusId: string) => void;
  statusConfigs?: StatusConfig[];
  onUpdatePriority?: (priority: string) => void;
  onUpdateDueDate?: (dueDate: string | null) => void;
  onUpdateAssignee?: (assigneeId: string | null) => void;
  projectMembers?: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  userId?: string;
  isDragging?: boolean;
  canEdit?: boolean;
  canDrag?: boolean;
  canToggle?: boolean;
  isNew?: boolean;
  onNewHighlightEnd?: () => void;
}

function SortableTask({ task, onClick, onToggle, onEdit, onDelete, onUpdateStatus, onUpdatePriority, onUpdateDueDate, onUpdateAssignee, projectMembers, userId, isDragging, canEdit, canDrag = true, canToggle = false, isNew, onNewHighlightEnd, statusConfigs }: SortableTaskProps) {
  const isCompleted = task.status === 'COMPLETED';
  const isInProgress = task.status === 'IN_PROGRESS';
  const isAssignee = task.assigneeId === userId;
  // Compare dates in UTC to avoid timezone issues
  const isOverdue = task.dueDate && (() => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const dueDateUTC = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    return dueDateUTC < todayUTC;
  })() && !isCompleted;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: task.id,
    disabled: isCompleted || !canDrag, // Disable dragging for completed tasks or if user can't drag
    // Skip post-drop layout animation — the optimistic cache update already
    // repositions the row in DOM, and dnd-kit's own transition racing against
    // that causes jitter.
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const elementRef = useRef<HTMLDivElement | null>(null);
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    elementRef.current = node;
  }, [setNodeRef]);

  useEffect(() => {
    if (isNew && elementRef.current) {
      elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const timer = setTimeout(() => onNewHighlightEnd?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;

  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const priorityMenuRef = useRef<HTMLDivElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const dueDateRef = useRef<HTMLInputElement>(null);
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const assigneeMenuRef = useRef<HTMLDivElement>(null);
  const assigneeSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showAssigneeMenu && assigneeSearchRef.current) {
      assigneeSearchRef.current.focus();
    }
  }, [showAssigneeMenu]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (priorityMenuRef.current && !priorityMenuRef.current.contains(event.target as Node)) {
        setShowPriorityMenu(false);
      }
      if (assigneeMenuRef.current && !assigneeMenuRef.current.contains(event.target as Node)) {
        setShowAssigneeMenu(false);
        setAssigneeSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={combinedRef}
      style={style}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-2 p-3 rounded-lg border border-grey-200 hover:border-grey-300 transition-colors',
        isDragging && 'opacity-0',
        isOverdue ? 'bg-error/5' : 'bg-white',
        onClick && 'cursor-pointer',
        isNew && 'border-2 border-orange-400'
      )}
    >
      {/* Drag Handle */}
      {canDrag && (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-grey-300 hover:text-grey-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}

      {/* Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (canToggle) onToggle();
        }}
        disabled={!canToggle}
        className={cn(
          "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          isCompleted
            ? "bg-success border-success text-white"
            : canToggle
              ? "border-grey-300 hover:border-primary cursor-pointer"
              : "border-grey-200 cursor-not-allowed opacity-50"
        )}
      >
        {isCompleted && <Check className="w-3 h-3" />}
      </button>

      {/* Task Title */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <p className={cn(
          'text-sm font-medium truncate',
          isCompleted ? 'text-grey-400' : isOverdue ? 'text-error' : 'text-secondary'
        )}>
          {task.title}
        </p>

        {/* Comments count - inline with title */}
        {(task._count?.comments || 0) > 0 && (
          <div className="flex items-center gap-1 text-xs text-grey-400 shrink-0">
            <MessageSquare className="w-3 h-3" />
            {task._count.comments}
          </div>
        )}
      </div>

      {/* Assignee Column */}
      <div className="w-[100px] flex-shrink-0 flex justify-center relative" ref={assigneeMenuRef}>
        {canEdit && onUpdateAssignee ? (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAssigneeMenu(!showAssigneeMenu); }}
              className="rounded-full hover:ring-2 hover:ring-grey-200 transition-all"
            >
              {task.assignee ? (
                <Avatar
                  src={task.assignee.avatarUrl}
                  name={task.assignee.name}
                  size="xs"
                />
              ) : (
                <UserCircle className="w-5 h-5 text-grey-300" />
              )}
            </button>
            {showAssigneeMenu && (
              <div className="absolute top-full mt-1 w-52 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50 right-0">
                <div className="px-2 pb-1">
                  <input
                    ref={assigneeSearchRef}
                    type="text"
                    placeholder="Search..."
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-2 py-1 text-xs border border-grey-200 rounded focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {task.assigneeId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateAssignee(null);
                        setShowAssigneeMenu(false);
                        setAssigneeSearch('');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-grey-500 hover:bg-grey-50 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Unassign
                    </button>
                  )}
                  {(projectMembers || [])
                    .filter((m) => m.user.name.toLowerCase().includes(assigneeSearch.toLowerCase()))
                    .map((member) => (
                      <button
                        key={member.user.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateAssignee(member.user.id);
                          setShowAssigneeMenu(false);
                          setAssigneeSearch('');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-grey-50 transition-colors',
                          task.assigneeId === member.user.id ? 'font-medium text-primary' : 'text-secondary'
                        )}
                      >
                        <Avatar
                          src={member.user.avatarUrl}
                          name={member.user.name}
                          size="xs"
                        />
                        {member.user.name}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </>
        ) : (
          task.assignee ? (
            <Avatar
              src={task.assignee.avatarUrl}
              name={task.assignee.name}
              size="xs"
            />
          ) : (
            <UserCircle className="w-5 h-5 text-grey-300" />
          )
        )}
      </div>

      {/* Status Column */}
      {/* Status Column */}
      <div className="w-[100px] flex-shrink-0 flex justify-center relative">
        <StatusPicker
          statusConfig={(task as any).statusConfig ?? null}
          statusConfigs={statusConfigs ?? []}
          onChange={onUpdateStatus}
          stopDragPointerDown={false}
        />
      </div>

      {/* Priority Column */}
      <div className="w-[80px] flex-shrink-0 flex justify-center relative" ref={priorityMenuRef}>
        {canEdit && onUpdatePriority ? (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setShowPriorityMenu(!showPriorityMenu); }}
              className={cn(
                'text-xs px-2 py-0.5 rounded hover:bg-grey-100 transition-colors',
                isCompleted ? 'text-grey-400' : priority.color
              )}
            >
              {priority.label}
            </button>
            {showPriorityMenu && (
              <div className="absolute top-full mt-1 w-28 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                {(Object.entries(priorityConfig) as [string, { color: string; label: string }][]).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdatePriority(key);
                      setShowPriorityMenu(false);
                    }}
                    className={cn(
                      'w-full text-left px-3 py-1.5 text-xs hover:bg-grey-50 transition-colors',
                      task.priority === key ? 'font-medium' : '',
                      config.color
                    )}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className={cn('text-xs', isCompleted ? 'text-grey-400' : priority.color)}>
            {priority.label}
          </span>
        )}
      </div>

      {/* Due Date Column */}
      <div className="w-[100px] flex-shrink-0 flex justify-center">
        {canEdit && onUpdateDueDate ? (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                dueDateRef.current?.showPicker();
              }}
              className={cn(
                'flex items-center gap-1 text-xs cursor-pointer rounded hover:bg-grey-100 px-2 py-1 transition-colors',
                isCompleted ? 'text-grey-400' : task.dueDate ? (isOverdue ? 'text-error' : 'text-grey-500') : 'text-grey-300'
              )}
            >
              {task.dueDate && isOverdue && <AlertCircle className="w-3 h-3" />}
              {!task.dueDate && <Calendar className="w-3 h-3" />}
              {task.dueDate
                ? new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
                : ''
              }
            </button>
            <input
              ref={dueDateRef}
              type="date"
              className="absolute inset-0 opacity-0 pointer-events-none w-0 h-0"
              value={task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                e.stopPropagation();
                onUpdateDueDate(e.target.value || null);
              }}
              tabIndex={-1}
            />
          </div>
        ) : (
          <div className={cn(
            'flex items-center gap-1 text-xs',
            isCompleted ? 'text-grey-400' : task.dueDate ? (isOverdue ? 'text-error' : 'text-grey-500') : 'text-grey-300'
          )}>
            {task.dueDate && isOverdue && <AlertCircle className="w-3 h-3" />}
            {!task.dueDate && <Calendar className="w-3 h-3" />}
            {task.dueDate
              ? new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
              : ''
            }
          </div>
        )}
      </div>

      {/* Actions */}
      {canEdit && <TaskActions onEdit={onEdit} onDelete={onDelete} />}
    </div>
  );
}

// Task overlay for drag preview
function TaskOverlay({ task }: { task: any }) {
  const isCompleted = task.status === 'COMPLETED';

  return (
    <div className="flex items-center gap-2 p-3 rounded-lg border border-accent/30 bg-white shadow-lg">
      <GripVertical className="w-4 h-4 text-grey-300" />
      <div className={cn(
        'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center',
        isCompleted ? 'bg-success border-success text-white' : 'border-grey-300'
      )}>
        {isCompleted && <Check className="w-3 h-3" />}
      </div>
      <p className={cn(
        'text-sm font-medium',
        isCompleted ? 'text-grey-400 line-through' : 'text-secondary'
      )}>
        {task.title}
      </p>
    </div>
  );
}

// Visibility badge for non-shared lists
function VisibilityBadge({ visibility, isAgencyMember }: { visibility: string; isAgencyMember?: boolean }) {
  if (visibility === 'shared') return null;

  if (visibility === 'agency_only') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
        <Lock className="w-3 h-3" />
        Solid Only
      </span>
    );
  }

  if (visibility === 'client_only') {
    if (isAgencyMember) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-warning/10 text-warning" title="Client's private list — view only">
          <Eye className="w-3 h-3" />
          Client Only
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-accent/10 text-accent">
        <Users className="w-3 h-3" />
        My Team
      </span>
    );
  }

  return null;
}

// Task List Card with droppable area
interface TaskListCardProps {
  list: any;
  projectId: string;
  onTaskClick: (task: any) => void;
  onToggleTask: (taskId: string) => void;
  onEditTask: (task: any) => void;
  onDeleteTask: (taskId: string) => void;
  onUpdateTaskStatus: (taskId: string, statusId: string) => void;
  statusConfigs?: StatusConfig[];
  onUpdateTaskPriority: (taskId: string, priority: string) => void;
  onUpdateTaskDueDate: (taskId: string, dueDate: string | null) => void;
  onUpdateTaskAssignee: (taskId: string, assigneeId: string | null) => void;
  projectMembers: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  onAddTask: () => void;
  onEditList: () => void;
  onDeleteList: () => void;
  onArchiveList?: () => void;
  onUnarchiveList?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  activeId: string | null;
  userId?: string;
  isAgencyMember?: boolean;
  isAgencyAdmin?: boolean;
  isSystemAdmin?: boolean;
  isClientAdmin?: boolean;
  isArchived?: boolean;
  completingTaskIds?: Set<string>;
  onTaskAnimationComplete?: (taskId: string) => void;
  newTaskId?: string | null;
  onNewHighlightEnd?: () => void;
  canReorderLists?: boolean;
  isFiltering?: boolean;
  compact?: boolean;
  onOpenListDetail?: (listId: string, assigneeId?: string | null) => void;
  sortBy: SortOption;
  filterAssignee: string | null;
  canCreateTask?: boolean;
}

function TaskListCard({
  list,
  projectId,
  onTaskClick,
  onToggleTask,
  onEditTask,
  onDeleteTask,
  onUpdateTaskStatus,
  onUpdateTaskPriority,
  onUpdateTaskDueDate,
  onUpdateTaskAssignee,
  projectMembers,
  onAddTask,
  onEditList,
  onDeleteList,
  onArchiveList,
  onUnarchiveList,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  activeId,
  userId,
  isAgencyMember,
  canCreateTask = true,
  isAgencyAdmin,
  isSystemAdmin,
  isClientAdmin,
  isArchived,
  completingTaskIds,
  onTaskAnimationComplete,
  newTaskId,
  onNewHighlightEnd,
  canReorderLists,
  isFiltering,
  statusConfigs,
  compact,
  onOpenListDetail,
  sortBy,
  filterAssignee,
}: TaskListCardProps) {
  const navigate = useNavigate();
  const openDetail = () =>
    onOpenListDetail
      ? onOpenListDetail(list.id, filterAssignee)
      : navigate(`/projects/${projectId}/tasks/${list.id}`);
  const tasks = list.tasks || [];
  const [isCollapsed, setIsCollapsed] = useState(false);

  // List-level drag-and-drop
  const listDragDisabled = !canReorderLists || !!isFiltering;
  const {
    attributes: listAttributes,
    listeners: listListeners,
    setNodeRef: setListNodeRef,
    transform: listTransform,
    transition: listTransition,
    isDragging: isListDragging,
  } = useSortable({ id: list.id, disabled: listDragDisabled });

  const listStyle = {
    transform: CSS.Transform.toString(listTransform),
    transition: listTransition,
  };

  // Visibility-aware permissions:
  // - agency_only lists: only agency members can modify
  // - client_only lists: only clients can modify (unless system admin)
  // - shared lists: anyone with project access
  const listVisibility = list.visibility || 'shared';
  const isReadOnlyForUser =
    (listVisibility === 'client_only' && isAgencyMember && !isSystemAdmin) ||
    (listVisibility === 'agency_only' && !isAgencyMember);

  // Permission: can edit list if not archived, not read-only, and has appropriate role
  const canEditList = !isArchived && !isReadOnlyForUser && (isAgencyMember || list.createdById === userId);
  // Permission: can delete list if agency admin (and not archived, and not read-only)
  const canDeleteList = !isArchived && !isReadOnlyForUser && isAgencyAdmin;

  // State for list actions menu
  const [showListMenu, setShowListMenu] = useState(false);
  const listMenuRef = useRef<HTMLDivElement>(null);

  // Close list menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (listMenuRef.current && !listMenuRef.current.contains(event.target as Node)) {
        setShowListMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Permission helpers for tasks:
  // - If the list is read-only for this user, no editing/toggling
  // - Agency members: can edit any task (in non-read-only lists)
  // - Clients: can only edit tasks they created
  // - No one can edit if project is archived
  const canEditTask = (task: any) => {
    if (isArchived || isReadOnlyForUser) return false;
    if (isAgencyMember) return true;
    return task.createdById === userId;
  };

  // Permission for toggling task completion:
  // - If the list is read-only for this user, no toggling
  // - Agency members: can toggle any task
  // - Clients: can only toggle tasks assigned to them
  // - No one can toggle if project is archived
  const canToggleTask = (task: any) => {
    if (isArchived || isReadOnlyForUser) return false;
    if (isAgencyMember) return true;
    return task.assigneeId === userId;
  };

  const canDragTasks = !isArchived && !isReadOnlyForUser && !!isAgencyMember; // Only agency members can reorder tasks
  const completedCount = tasks.filter((t: any) => t.status === 'COMPLETED').length;
  const totalCount = tasks.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Show/hide completed tasks (hidden by default)
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);

  // Sort + assignee filter are lifted to the page level (apply to all lists).

  // Get unique assignees from this list's tasks
  const assignees = tasks.reduce((acc: any[], task: any) => {
    if (task.assignee && !acc.find((a) => a.id === task.assignee.id)) {
      acc.push(task.assignee);
    }
    return acc;
  }, []);

  const selectedAssigneeName = assignees.find((a) => a.id === filterAssignee)?.name;

  // Sorting function
  const sortTasks = (tasksToSort: any[]) => {
    if (sortBy === 'default') return tasksToSort;

    return [...tasksToSort].sort((a, b) => {
      switch (sortBy) {
        case 'due-date-asc':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case 'due-date-desc':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
        case 'priority': {
          const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 2;
          const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 2;
          return aPriority - bPriority;
        }
        default:
          return 0;
      }
    });
  };

  // Filter and sort tasks
  const getProcessedTasks = (tasksToProcess: any[]) => {
    let filtered = tasksToProcess;
    if (filterAssignee) {
      filtered = tasksToProcess.filter((t) => t.assigneeId === filterAssignee);
    }
    return sortTasks(filtered);
  };

  // Filter tasks based on completed visibility toggle
  const visibleTasks = showCompletedTasks
    ? tasks
    : tasks.filter((t: any) => t.status !== 'COMPLETED');
  const processedTasks = getProcessedTasks(visibleTasks);
  // On the landing, the compact board hides closed/completed tasks by default
  // (like the list view) — unless the list's "Show Completed Tasks" toggle is on.
  const compactTasks = getProcessedTasks(
    showCompletedTasks ? tasks : tasks.filter((t: any) => t.status !== 'COMPLETED'),
  );
  // Collapse isn't offered in the compact view — force the body open there.
  const effectiveCollapsed = isCollapsed && !compact;
  // Footer: how many tasks are currently visible vs. total in the list.
  const visibleCount = compact ? compactTasks.length : processedTasks.length;

  const hasFilters = sortBy !== 'default' || filterAssignee;

  // Make the list a droppable target
  const { setNodeRef, isOver } = useDroppable({
    id: `list-${list.id}`,
    data: { listId: list.id },
  });

  return (
    <div ref={setListNodeRef} style={listStyle} className={cn('group relative', isListDragging && 'opacity-50')}>
      {/* Drag handle — a drawer that slides out from underneath the card's left edge */}
      {canReorderLists && !isFiltering && (
        <button
          {...listAttributes}
          {...listListeners}
          className={cn(
            'absolute left-0 top-[12.5%] bottom-[12.5%] flex w-7 items-center justify-center rounded-l-lg border border-r-0 border-grey-200 bg-grey-50 text-grey-400 shadow-sm hover:text-grey-600 cursor-grab active:cursor-grabbing transition-transform duration-200',
            isListDragging ? '-translate-x-[75%]' : 'translate-x-0 group-hover:-translate-x-[75%]',
          )}
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      <Card
        className="relative flex flex-col h-full cursor-pointer"
        onClick={(e) => {
          // Clicking whitespace opens the board (like discussion cards). Skip
          // interactive controls and the list-view task rows.
          const el = e.target as HTMLElement;
          if (el.closest('button, a, input, textarea, select, label, [role="button"], [contenteditable="true"], [data-status-dropdown], [data-no-card-open]')) return;
          openDetail();
        }}
      >
      {/* List Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {!compact && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1 hover:bg-grey-100 rounded transition-colors"
              title={isCollapsed ? 'Expand list' : 'Collapse list'}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4 text-grey-400" /> : <ChevronDown className="w-4 h-4 text-grey-400" />}
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openDetail}
                className={cn(
                  "font-semibold transition-colors text-left hover:underline decoration-1 underline-offset-2",
                  list.archivedAt ? "text-grey-400" : "text-secondary hover:text-primary"
                )}
                title="Open board"
              >
                {list.name}
              </button>
              <VisibilityBadge visibility={listVisibility} isAgencyMember={isAgencyMember} />
              {list.archivedAt && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-grey-400">
                  <Archive className="w-3 h-3" />
                  Archived
                </span>
              )}
            </div>
            {isCollapsed && !compact && (
              <p className="text-xs text-grey-500">
                {totalCount} task{totalCount !== 1 ? 's' : ''} \u00b7 {completedCount} completed
              </p>
            )}
          </div>

        </div>

        <div className="flex items-center gap-2">
          {/* Progress */}
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-grey-400">
                {totalCount - completedCount} left - {completedCount} done
              </span>
              <div className="w-12 h-1.5 bg-grey-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-success rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-grey-400">{progressPercent}%</span>
            </div>
          )}

          {/* List Actions Menu */}
          <div className="relative" ref={listMenuRef}>
          <button
            onClick={() => setShowListMenu(!showListMenu)}
            className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
            title="List options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showListMenu && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
              {!isArchived && !isReadOnlyForUser && canCreateTask && (
                <button
                  onClick={() => { onAddTask(); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
                >
                  <Plus className="w-4 h-4" />
                  Add a task
                </button>
              )}
              {completedCount > 0 && (
                <button
                  onClick={() => { setShowCompletedTasks(!showCompletedTasks); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
                >
                  {showCompletedTasks ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showCompletedTasks ? 'Hide Completed Tasks' : 'Show Completed Tasks'}
                </button>
              )}
              {canEditList && (
                <button
                  onClick={() => { onEditList(); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              )}
              {canDeleteList && !list.archivedAt && onArchiveList && (
                <button
                  onClick={() => { onArchiveList(); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </button>
              )}
              {canDeleteList && list.archivedAt && onUnarchiveList && (
                <button
                  onClick={() => { onUnarchiveList(); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
                >
                  <ArchiveRestore className="w-4 h-4" />
                  Unarchive
                </button>
              )}
              {canDeleteList && (
                <button
                  onClick={() => { onDeleteList(); setShowListMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {!effectiveCollapsed && (
      <>
      {/* Column Headers */}
      {!compact && processedTasks.length > 0 && (
        <div className="flex items-center gap-2 px-3 pb-2 border-b border-grey-100 mb-2">
          {/* Spacer for drag handle + checkbox */}
          <div className="w-[52px] flex-shrink-0" />
          {/* Task title header */}
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Task</span>
          </div>
          <div className="w-[100px] flex-shrink-0 text-center">
            <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Assignee</span>
          </div>
          <div className="w-[100px] flex-shrink-0 text-center">
            <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Status</span>
          </div>
          <div className="w-[80px] flex-shrink-0 text-center">
            <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Priority</span>
          </div>
          <div className="w-[100px] flex-shrink-0 text-center">
            <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Due Date</span>
          </div>
          {/* Spacer for actions */}
          <div className="w-[28px] flex-shrink-0" />
        </div>
      )}

      {/* Task List */}
      {compact ? (
        <div className="pt-1">
          <CompactBoard
            listId={list.id}
            tasks={compactTasks}
            statusConfigs={showCompletedTasks ? (statusConfigs ?? []) : (statusConfigs ?? []).filter((s) => !s.isClosed)}
            onTaskClick={(t: any) => onTaskClick({ ...t, listId: list.id })}
            onSeeAll={openDetail}
            draggable={canDragTasks}
          />
        </div>
      ) : (
      <SortableContext
        items={processedTasks.map((t: any) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          data-no-card-open
          className={cn(
            'space-y-2 min-h-[60px] rounded-lg transition-colors p-1 -m-1 flex-1',
            (activeId || isOver) && 'bg-grey-50/50 border border-dashed border-grey-200',
            isOver && 'border-accent/50 bg-accent/5'
          )}
        >
          {processedTasks.map((task: any) => {
            const isCompleting = completingTaskIds?.has(task.id);
            return (
              <div key={task.id} className="relative" style={isCompleting ? { overflow: 'visible' } : undefined}>
                <div className={cn(isCompleting && 'animate-poof-row pointer-events-none')}>
                  <SortableTask
                    task={task}
                    onClick={() => onTaskClick({ ...task, listId: list.id })}
                    onToggle={() => onToggleTask(task.id)}
                    onEdit={() => onEditTask(task)}
                    onDelete={() => onDeleteTask(task.id)}
                    onUpdateStatus={isAgencyMember ? (status) => onUpdateTaskStatus(task.id, status) : undefined}
                    onUpdatePriority={isAgencyMember ? (priority) => onUpdateTaskPriority(task.id, priority) : undefined}
                    onUpdateDueDate={isAgencyMember ? (dueDate) => onUpdateTaskDueDate(task.id, dueDate) : undefined}
                    onUpdateAssignee={isAgencyMember ? (assigneeId) => onUpdateTaskAssignee(task.id, assigneeId) : undefined}
                    projectMembers={isAgencyMember ? projectMembers : undefined}
                    userId={userId}
                    isDragging={activeId === task.id}
                    canEdit={canEditTask(task)}
                    canDrag={canDragTasks}
                    canToggle={canToggleTask(task)}
                    isNew={task.id === newTaskId}
                    onNewHighlightEnd={onNewHighlightEnd}
                    statusConfigs={statusConfigs}
                  />
                </div>
                <CompletionCelebration
                  show={!!isCompleting}
                  onComplete={() => onTaskAnimationComplete?.(task.id)}
                />
              </div>
            );
          })}

          {/* Empty state */}
          {tasks.length === 0 && (
            <div className="text-center py-4 text-sm text-grey-400">
              No tasks yet
            </div>
          )}
        </div>
      </SortableContext>
      )}

      {/* Footer — Add task + visible/total count, with a centered "expand" hint */}
      <div className="relative mt-auto pt-4 flex items-center justify-between gap-3">
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center pt-4 text-xs text-grey-300 opacity-0 transition-opacity group-hover:opacity-100">
          Click to expand
        </span>
        {!isArchived && !isReadOnlyForUser && canCreateTask ? (
          <button
            onClick={onAddTask}
            className="flex items-center gap-2 text-sm text-grey-500 hover:text-primary transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add a task
          </button>
        ) : (
          <span />
        )}
        {totalCount > 0 && (
          <span className="text-xs text-grey-400">
            Showing {visibleCount} of {totalCount} task{totalCount !== 1 ? 's' : ''}
            {filterAssignee && selectedAssigneeName ? ` · ${selectedAssigneeName}` : ''}
          </span>
        )}
      </div>
      </>
      )}
      </Card>
    </div>
  );
}

export function ProjectTaskLists() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Admin / staff of THIS project: Solid anywhere, a partner agency on its own
  const { isProjectAdmin: isAgencyAdmin, isProjectStaff: isAgencyMember } = useProjectRoles(projectId);
  const isSystemAdmin = getIsSystemAdmin(user);

  // Check if user is a client admin (admin of a non-agency organization)
  const isClientAdmin = !isAgencyMember && user?.organizations?.some(
    (org: any) => org.type !== 'AGENCY' && (org.role === 'client_admin' || org.role === 'Client Admin' || org.isOwner)
  );

  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [newTaskId, setNewTaskId] = useState<string | null>(null);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [editingList, setEditingList] = useState<any | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteListConfirmId, setDeleteListConfirmId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<any | null>(null);
  // Non-null (a color) while a compact-board pill is being dragged — drives the
  // pill-shaped drag overlay and the cross-list drop handler.
  const [compactDragColor, setCompactDragColor] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [takeover, setTakeover] = useState<{ listId: string; assigneeId: string | null } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [taskView, setTaskView] = useState<TaskView>(() =>
    localStorage.getItem('solidhub-tasklists-view') === 'compact' ? 'compact' : 'list',
  );
  useEffect(() => {
    localStorage.setItem('solidhub-tasklists-view', taskView);
  }, [taskView]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'incomplete' | 'completed'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Page-level sort + assignee filter — applies to every list in this view.
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) setShowSortMenu(false);
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) setShowFilterMenu(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch project for name and archived check
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const isArchived = !!project?.archivedAt;

  // Fetch task lists with tasks
  const { data: taskLists, isLoading } = useQuery({
    queryKey: ['taskLists', projectId, showArchived],
    queryFn: () => tasksApi.listTaskLists(projectId!, showArchived),
    enabled: !!projectId,
  });

  const { data: statusConfigs = [] } = useTaskStatusConfigs();

  // Filter task lists based on search query and status filter
  const filteredTaskLists = taskLists?.map((list: any) => {
    const filteredTasks = list.tasks?.filter((task: any) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const titleMatch = task.title?.toLowerCase().includes(query);
        const descMatch = task.description?.toLowerCase().includes(query);
        if (!titleMatch && !descMatch) return false;
      }
      // Status filter
      if (statusFilter === 'incomplete' && task.status === 'COMPLETED') return false;
      if (statusFilter === 'completed' && task.status !== 'COMPLETED') return false;
      return true;
    });
    return { ...list, tasks: filteredTasks };
  }).filter((list: any) => {
    // If searching or filtering, hide empty lists
    if (searchQuery || statusFilter !== 'all') {
      return list.tasks && list.tasks.length > 0;
    }
    return true;
  });

  const projectMembers = project?.members || [];
  // Clients may only create tasks when the project allows it; agency always can.
  const clientsCanCreate = (project?.settings as any)?.clientsCanCreateTasks !== false;
  const canCreateTask = isAgencyMember || clientsCanCreate;
  // Page-level assignee options — union of assignees across every list's tasks.
  const assignees = (() => {
    const map = new Map<string, any>();
    (taskLists || []).forEach((l: any) =>
      (l.tasks || []).forEach((t: any) => {
        if (t.assignee && !map.has(t.assignee.id)) map.set(t.assignee.id, t.assignee);
      }),
    );
    return [...map.values()];
  })();
  const selectedAssigneeName = assignees.find((a: any) => a.id === filterAssignee)?.name;
  const hasFilters = sortBy !== 'default' || !!filterAssignee;

  // Open create modal when navigated with openCreate state (e.g. from sidebar)
  useEffect(() => {
    if ((location.state as any)?.openCreate && taskLists) {
      setShowCreateTaskModal(true);
      nav(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, nav, taskLists]);

  // Sync selectedTask with ?taskId= URL param
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (!taskId || !taskLists) return;
    const task = findTaskById(taskId);
    if (task && (!selectedTask || selectedTask.id !== taskId)) {
      setSelectedTask(task);
    }
  }, [searchParams, taskLists]);

  const handleTaskClick = (task: any) => {
    setSelectedTask(task);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('taskId', task.id);
      return next;
    });
  };

  // Track tasks completing with animation
  const [completingTaskIds, setCompletingTaskIds] = useState<Set<string>>(new Set());

  // Toggle task completion
  const toggleTaskMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.toggleComplete(taskId),
    onSuccess: (_, taskId) => {
      // Find the task to check if it was being completed (not un-completed)
      const task = findTaskById(taskId);
      const wasCompleted = task?.status === 'COMPLETED';

      if (wasCompleted) {
        // Un-completing: just refresh immediately
        queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      } else {
        // Completing: play canvas animation (onComplete callback handles refresh)
        setCompletingTaskIds(prev => new Set(prev).add(taskId));
      }
    },
  });

  // Move task mutation - optimistic update is done in handleDragEnd
  const moveTaskMutation = useMutation({
    mutationFn: ({ taskId, listId, position }: { taskId: string; listId: string; position?: number }) =>
      tasksApi.moveTask(taskId, listId, position),
    onError: () => {
      // Refetch to restore correct state on error
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
    onSettled: () => {
      // Refetch to ensure we're in sync with the server
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Compact board: move a task to a different list AND/OR status in one call.
  // Optimistic cache update is applied inline in handleDragEnd (like task moves).
  const compactMoveMutation = useMutation({
    mutationFn: ({ taskId, listId, statusId }: { taskId: string; listId: string; statusId: string }) =>
      tasksApi.updateTask(taskId, { listId, statusId }),
    onError: () => queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] }),
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => tasksApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      console.error('Failed to delete task:', error);
      setDeleteConfirmId(null);
    },
  });

  // Update task status (for In Progress toggle)
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, statusId }: { taskId: string; statusId: string }) =>
      tasksApi.updateTask(taskId, { statusId }),
    // Optimistically move the task to the new status so drag/drop on the compact
    // board (and status pickers) reflect instantly instead of waiting on refetch.
    onMutate: async ({ taskId, statusId }) => {
      await queryClient.cancelQueries({ queryKey: ['taskLists', projectId] });
      const previous = queryClient.getQueryData(['taskLists', projectId, showArchived]);
      const cfg = statusConfigs?.find((s) => s.id === statusId);
      queryClient.setQueryData<any[]>(['taskLists', projectId, showArchived], (old) =>
        old?.map((list: any) => ({
          ...list,
          tasks: list.tasks?.map((t: any) =>
            t.id === taskId ? { ...t, statusId, statusConfig: cfg ?? t.statusConfig } : t,
          ),
        })),
      );
      return { previous };
    },
    onError: (_e, _v, ctx: any) => {
      if (ctx?.previous) queryClient.setQueryData(['taskLists', projectId, showArchived], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Update task priority
  const updatePriorityMutation = useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: string }) =>
      tasksApi.updateTask(taskId, { priority }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Update task due date
  const updateDueDateMutation = useMutation({
    mutationFn: ({ taskId, dueDate }: { taskId: string; dueDate: string | null }) =>
      tasksApi.updateTask(taskId, { dueDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Update task assignee
  const updateAssigneeMutation = useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string | null }) =>
      tasksApi.updateTask(taskId, { assigneeId: assigneeId ?? null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Reorder task lists mutation
  const reorderListsMutation = useMutation({
    mutationFn: (listIds: string[]) => tasksApi.reorderTaskLists(projectId!, listIds),
    onMutate: async (listIds) => {
      await queryClient.cancelQueries({ queryKey: ['taskLists', projectId] });
      const previous = queryClient.getQueryData<any[]>(['taskLists', projectId, showArchived]);
      if (previous) {
        const reordered = listIds
          .map((id) => previous.find((l: any) => l.id === id))
          .filter(Boolean);
        queryClient.setQueryData(['taskLists', projectId, showArchived], reordered);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['taskLists', projectId, showArchived], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  const handleMoveList = (listId: string, direction: 'up' | 'down') => {
    if (!taskLists) return;
    const currentIndex = taskLists.findIndex((l: any) => l.id === listId);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= taskLists.length) return;
    const newOrder = [...taskLists];
    [newOrder[currentIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[currentIndex]];
    reorderListsMutation.mutate(newOrder.map((l: any) => l.id));
  };

  // Delete task list mutation
  const deleteTaskListMutation = useMutation({
    mutationFn: (listId: string) => tasksApi.deleteTaskList(projectId!, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setDeleteListConfirmId(null);
    },
    onError: (error) => {
      console.error('Failed to delete task list:', error);
      setDeleteListConfirmId(null);
    },
  });

  // Archive task list mutation
  const archiveTaskListMutation = useMutation({
    mutationFn: (listId: string) => tasksApi.archiveTaskList(projectId!, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Unarchive task list mutation
  const unarchiveTaskListMutation = useMutation({
    mutationFn: (listId: string) => tasksApi.unarchiveTaskList(projectId!, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Find which list contains a task
  const findListByTaskId = (taskId: string) => {
    if (!taskLists) return null;
    for (const list of taskLists) {
      if (list.tasks?.some((t: any) => t.id === taskId)) {
        return list;
      }
    }
    return null;
  };

  // Find task by ID
  const findTaskById = (taskId: string) => {
    if (!taskLists) return null;
    for (const list of taskLists) {
      const task = list.tasks?.find((t: any) => t.id === taskId);
      if (task) return task;
    }
    return null;
  };

  // Collision strategy: compact-board pills only consider compact status columns
  // (so they can drop across lists); everything else uses the default.
  const collisionDetectionStrategy: CollisionDetection = (args) => {
    if (args.active.data.current?.type === 'compact-pill') {
      const columns = args.droppableContainers.filter(
        (c) => c.data.current?.type === 'compact-column',
      );
      return pointerWithin({ ...args, droppableContainers: columns });
    }
    return closestCorners(args);
  };

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    setActiveTask(findTaskById(active.id as string));
    const data = active.data.current;
    setCompactDragColor(data?.type === 'compact-pill' ? (data.color ?? null) : null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeList = findListByTaskId(active.id as string);
    const overList = findListByTaskId(over.id as string);

    // If dragging over a different list's task, we'll handle in dragEnd
    if (activeList && overList && activeList.id !== overList.id) {
      // Visual feedback could be added here
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Compact-board pill dropped on a status column (possibly in another list).
    if (active.data.current?.type === 'compact-pill') {
      const clear = () => {
        setActiveId(null);
        setActiveTask(null);
        setCompactDragColor(null);
      };
      const overData = over?.data.current;
      if (!over || overData?.type !== 'compact-column') return clear();
      const taskId = active.id as string;
      const srcList = active.data.current.listId as string;
      const srcStatus = active.data.current.statusId as string;
      const dstList = overData.listId as string;
      const dstStatus = overData.statusId as string;
      if (dstList === srcList && dstStatus === srcStatus) return clear();

      // Optimistically move the task to the target list + status.
      const cfg = statusConfigs?.find((s) => s.id === dstStatus);
      queryClient.setQueryData<any[]>(['taskLists', projectId, showArchived], (old) => {
        if (!old) return old;
        let moved: any = null;
        const stripped = old.map((list: any) => {
          const tasks = (list.tasks || []).filter((t: any) => {
            if (t.id === taskId) { moved = t; return false; }
            return true;
          });
          return { ...list, tasks };
        });
        if (!moved) return old;
        const next = { ...moved, listId: dstList, statusId: dstStatus, statusConfig: cfg ?? moved.statusConfig };
        return stripped.map((list: any) =>
          list.id === dstList ? { ...list, tasks: [...list.tasks, next] } : list,
        );
      });
      clear();
      compactMoveMutation.mutate({ taskId, listId: dstList, statusId: dstStatus });
      return;
    }

    if (!over) {
      setActiveId(null);
      setActiveTask(null);
      return;
    }

    const activeId_str = active.id as string;
    const overId = over.id as string;

    // Check if this is a list-level drag
    const isListDrag = taskLists?.some((l: any) => l.id === activeId_str);
    if (isListDrag) {
      const activeIndex = taskLists!.findIndex((l: any) => l.id === activeId_str);
      const overIndex = taskLists!.findIndex((l: any) => l.id === overId);
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const newOrder = arrayMove(taskLists!, activeIndex, overIndex);
        reorderListsMutation.mutate(newOrder.map((l: any) => l.id));
      }
      setActiveId(null);
      setActiveTask(null);
      return;
    }

    const activeTaskId = activeId_str;

    const activeList = findListByTaskId(activeTaskId);
    let targetListId: string | null = null;
    let position: number | undefined;

    // Check if dropped over another task
    const overList = findListByTaskId(overId);
    if (overList) {
      targetListId = overList.id;
      // Find position based on where it was dropped (only count open tasks)
      const openTasks = (overList.tasks || []).filter((t: any) => t.status !== 'COMPLETED');
      const overIndex = openTasks.findIndex((t: any) => t.id === overId);
      position = overIndex >= 0 ? overIndex : openTasks.length;
    }

    // Check if dropped over a list container (droppable with id like "list-{listId}")
    if (!targetListId) {
      if (overId.startsWith('list-')) {
        targetListId = overId.replace('list-', '');
        position = 0;
      } else {
        // Fallback: check for data-list-id attribute
        const element = document.querySelector(`[data-list-id="${overId}"]`);
        if (element) {
          targetListId = overId;
          position = 0;
        }
      }
    }

    // If we found a target and it's different from source, move the task
    if (targetListId && activeList && (activeList.id !== targetListId || position !== undefined)) {
      // Update cache SYNCHRONOUSLY before clearing drag state
      const previousLists = queryClient.getQueryData<any[]>(['taskLists', projectId, showArchived]);
      if (previousLists) {
        const task = activeList.tasks?.find((t: any) => t.id === activeTaskId);
        if (task) {
          const newLists = previousLists.map((list: any) => {
            const newTasks = (list.tasks || []).filter((t: any) => t.id !== activeTaskId);

            if (list.id === targetListId) {
              const insertAt = position ?? newTasks.length;
              newTasks.splice(insertAt, 0, task);
            }

            return { ...list, tasks: newTasks };
          });
          queryClient.setQueryData(['taskLists', projectId, showArchived], newLists);
        }
      }

      // Now clear drag state - the task is already in its new position in the cache
      setActiveId(null);
      setActiveTask(null);

      // Call API to persist the change
      moveTaskMutation.mutate({
        taskId: activeTaskId,
        listId: targetListId,
        position,
      });
    } else {
      setActiveId(null);
      setActiveTask(null);
    }
  };

  const handleAddTask = (listId: string) => {
    setActiveListId(listId);
    setShowCreateTaskModal(true);
  };

  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-6 h-8 w-48" />
        <div className="grid grid-cols-1 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Delete task confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Task?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteTaskMutation.mutate(deleteConfirmId)}
                isLoading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete task list confirmation modal */}
      {deleteListConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteListConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Task List?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This will permanently delete the list and all tasks within it. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteListConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteTaskListMutation.mutate(deleteListConfirmId)}
                isLoading={deleteTaskListMutation.isPending}
              >
                Delete List
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <Card padding="sm" className="mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* New List Button - shown first on mobile */}
          {!isArchived && (
            <Button onClick={() => setShowCreateListModal(true)} size="sm" className="order-first sm:order-last">
              <Plus className="w-4 h-4" />
              New List
            </Button>
          )}

          {/* List / compact view toggle */}
          <div className="sm:order-last">
            <ViewToggle view={taskView} onViewChange={setTaskView} options={['list', 'compact']} />
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[140px] sm:flex-none sm:w-64 order-last sm:order-first">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="input pl-10 pr-8 text-sm w-full"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Toggle */}
          <div className="flex rounded-lg bg-grey-100 p-0.5">
            <button
              onClick={() => setStatusFilter('all')}
              className={cn(
                'px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                statusFilter === 'all' ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary'
              )}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('incomplete')}
              className={cn(
                'px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                statusFilter === 'incomplete' ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary'
              )}
            >
              Active
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={cn(
                'px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                statusFilter === 'completed' ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary'
              )}
            >
              Done
            </button>
          </div>

          {/* Sort (applies to all lists) */}
          <div className="relative" ref={sortMenuRef}>
            <button
              onClick={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false); }}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                sortBy !== 'default' ? 'bg-secondary text-white' : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span className="hidden sm:inline">{sortBy !== 'default' ? sortLabels[sortBy] : 'Sort'}</span>
            </button>
            {showSortMenu && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                {(Object.keys(sortLabels) as SortOption[]).map((option) => (
                  <button
                    key={option}
                    onClick={() => { setSortBy(option); setShowSortMenu(false); }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                      sortBy === option ? 'text-primary font-medium' : 'text-secondary'
                    )}
                  >
                    {sortLabels[option]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee filter (applies to all lists) */}
          {assignees.length > 0 && (
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false); }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  filterAssignee ? 'bg-secondary text-white' : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                )}
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">{selectedAssigneeName || 'Assignee'}</span>
              </button>
              {showFilterMenu && (
                <div className="absolute left-0 top-full mt-1 w-56 max-h-72 overflow-y-auto bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                  <button
                    onClick={() => { setFilterAssignee(null); setShowFilterMenu(false); }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                      !filterAssignee ? 'text-primary font-medium' : 'text-secondary'
                    )}
                  >
                    All Assignees
                  </button>
                  {assignees.map((assignee: any) => (
                    <button
                      key={assignee.id}
                      onClick={() => { setFilterAssignee(assignee.id); setShowFilterMenu(false); }}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                        filterAssignee === assignee.id ? 'text-primary font-medium' : 'text-secondary'
                      )}
                    >
                      <Avatar src={assignee.avatarUrl} name={assignee.name} size="xs" />
                      {assignee.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Clear sort/assignee */}
          {hasFilters && (
            <button
              onClick={() => { setSortBy('default'); setFilterAssignee(null); }}
              className="inline-flex items-center gap-1 px-2 py-2 text-sm text-grey-500 hover:text-secondary transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          )}

          {/* Show Archived Toggle */}
          {isAgencyAdmin && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                showArchived
                  ? 'bg-accent/10 text-accent'
                  : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              <Archive className="w-4 h-4" />
              <span className="hidden sm:inline">{showArchived ? 'Hide Archived' : 'Show Archived'}</span>
            </button>
          )}

          {/* Spacer - desktop only */}
          <div className="hidden sm:block flex-1" />
        </div>
      </Card>

      {/* Task Lists */}
      {!taskLists || taskLists.length === 0 ? (
        <Card>
          <EmptyState
            icon={CheckSquare}
            title="No task lists yet"
            description="Create your first task list to start organizing tasks."
            action={
              !isArchived ? (
                <Button onClick={() => setShowCreateListModal(true)}>
                  <Plus className="w-4 h-4" />
                  Create Task List
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : !filteredTaskLists || filteredTaskLists.length === 0 ? (
        <Card>
          <EmptyState
            icon={CheckSquare}
            title="No tasks found"
            description="Try adjusting your search or filters."
            action={
              <Button variant="outline" onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}>
                Clear filters
              </Button>
            }
          />
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetectionStrategy}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredTaskLists.map((l: any) => l.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid gap-6 grid-cols-1">
              {filteredTaskLists.map((list: any, index: number) => (
                <TaskListCard
                  key={list.id}
                  list={list}
                  projectId={projectId!}
                  onTaskClick={handleTaskClick}
                  onToggleTask={(taskId) => toggleTaskMutation.mutate(taskId)}
                  onEditTask={(task) => handleTaskClick(task)}
                  onDeleteTask={(taskId) => setDeleteConfirmId(taskId)}
                  onUpdateTaskStatus={(taskId, statusId) => updateStatusMutation.mutate({ taskId, statusId })}
                  statusConfigs={statusConfigs}
                  onUpdateTaskPriority={(taskId, priority) => updatePriorityMutation.mutate({ taskId, priority })}
                  onUpdateTaskDueDate={(taskId, dueDate) => updateDueDateMutation.mutate({ taskId, dueDate })}
                  onUpdateTaskAssignee={(taskId, assigneeId) => updateAssigneeMutation.mutate({ taskId, assigneeId })}
                  projectMembers={projectMembers}
                  onAddTask={() => handleAddTask(list.id)}
                  onEditList={() => setEditingList(list)}
                  onDeleteList={() => setDeleteListConfirmId(list.id)}
                  onArchiveList={() => archiveTaskListMutation.mutate(list.id)}
                  onUnarchiveList={() => unarchiveTaskListMutation.mutate(list.id)}
                  onMoveUp={() => handleMoveList(list.id, 'up')}
                  onMoveDown={() => handleMoveList(list.id, 'down')}
                  canMoveUp={!isArchived && isAgencyMember && index > 0}
                  canMoveDown={!isArchived && isAgencyMember && index < (filteredTaskLists?.length || 0) - 1}
                  activeId={activeId}
                  userId={user?.id}
                  isAgencyMember={isAgencyMember}
                  canCreateTask={canCreateTask}
                  isAgencyAdmin={isAgencyAdmin}
                  isSystemAdmin={isSystemAdmin}
                  isClientAdmin={isClientAdmin}
                  isArchived={isArchived}
                  completingTaskIds={completingTaskIds}
                  onTaskAnimationComplete={(taskId) => {
                    setCompletingTaskIds(prev => {
                      const next = new Set(prev);
                      next.delete(taskId);
                      return next;
                    });
                    queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
                  }}
                  newTaskId={newTaskId}
                  onNewHighlightEnd={() => setNewTaskId(null)}
                  canReorderLists={!!isAgencyMember && !isArchived}
                  isFiltering={!!searchQuery || statusFilter !== 'all'}
                  compact={taskView === 'compact'}
                  onOpenListDetail={(id, assigneeId) => setTakeover({ listId: id, assigneeId: assigneeId ?? null })}
                  sortBy={sortBy}
                  filterAssignee={filterAssignee}
                />
              ))}
            </div>
          </SortableContext>

          {/* Drag Overlay */}
          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              compactDragColor ? (
                <div
                  className="rounded-md border px-2 py-1 shadow-lg"
                  style={{ backgroundColor: `${compactDragColor}1f`, borderColor: `${compactDragColor}55` }}
                >
                  <span className="block truncate text-xs font-medium text-secondary">{activeTask.title}</span>
                </div>
              ) : (
                <TaskOverlay task={activeTask} />
              )
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Create List Modal */}
      <CreateTaskListModal
        isOpen={showCreateListModal}
        projectId={projectId!}
        onClose={() => setShowCreateListModal(false)}
        isAgencyMember={!!isAgencyMember}
        isSystemAdmin={isSystemAdmin}
      />

      {/* Edit List Modal */}
      {editingList && (
        <CreateTaskListModal
          isOpen={!!editingList}
          projectId={projectId!}
          onClose={() => setEditingList(null)}
          taskList={editingList}
          isAgencyMember={!!isAgencyMember}
          isSystemAdmin={isSystemAdmin}
        />
      )}

      {/* Create Task Modal */}
      {(activeListId || showCreateTaskModal) && (
        <TaskDetailModal
          create
          isOpen={showCreateTaskModal}
          onClose={() => {
            setShowCreateTaskModal(false);
            setActiveListId(null);
          }}
          projectId={projectId!}
          listId={activeListId || undefined}
          taskLists={!activeListId ? taskLists?.map((l: any) => ({ id: l.id, name: l.name })) : undefined}
          onTaskCreated={(id) => setNewTaskId(id)}
        />
      )}

      {/* Task List Takeover */}
      {takeover && projectId && (
        <TaskListTakeover
          listId={takeover.listId}
          projectId={projectId}
          initialFilterAssignee={takeover.assigneeId}
          onClose={() => setTakeover(null)}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => {
            setSelectedTask(null);
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.delete('taskId');
              return next;
            });
          }}
          task={selectedTask}
          projectId={projectId!}
          listId={selectedTask.listId}
          organizationId={project.organizationId}
        />
      )}
    </div>
  );
}
